import { FC, useContext, useEffect, useRef } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { Tables } from '@/supabase/types'
import { IconApps } from '@tabler/icons-react'
import { Badge } from '@/components/ui/badge'

import { LLM_LIST } from '@/lib/models/llm/llm-list'
import { validatePlanForTools } from '@/lib/subscription'

import { usePromptAndCommand } from './chat-hooks/use-prompt-and-command'
import { useDelayedPaywall } from '@/hooks/use-delayed-paywall'

interface ToolPickerProps {}

export const ToolPicker: FC<ToolPickerProps> = ({}) => {
  const {
    tools,
    platformTools,
    profile,
    focusTool,
    toolCommand,
    isToolPickerOpen,
    setIsToolPickerOpen,
    setIsPaywallOpen
  } = useContext(ChatbotUIContext)
  const scheduleUpgradePaywall = useDelayedPaywall(
    setIsPaywallOpen ? () => setIsPaywallOpen(true) : undefined
  )

  const { chatSettings } = useContext(ChatbotUIChatContext)

  const { handleSelectTool } = usePromptAndCommand()

  const itemsRef = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (focusTool && itemsRef.current[0]) {
      itemsRef.current[0].focus()
    }
  }, [focusTool])

  const toolsById = new Map<string, Tables<'tools'>>()
  ;[...tools, ...platformTools].forEach((tool) => {
    if (!tool?.id) return
    toolsById.set(tool.id, tool as Tables<'tools'>)
  })
  const allTools = Array.from(toolsById.values())

  const filteredTools = allTools.filter((tool) =>
    tool.name?.toLowerCase().includes(toolCommand?.toLowerCase())
  )

  const handleOpenChange = (isOpen: boolean) => {
    setIsToolPickerOpen(isOpen)
  }

  const callSelectTool = (tool: Tables<'tools'>) => {
    if (!validatePlanForTools(profile, [tool], chatSettings?.model)) {
      scheduleUpgradePaywall()
      return
    }
    handleSelectTool(tool)
    handleOpenChange(false)
  }

  const getKeyDownHandler = (index: number) => (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      handleOpenChange(false)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      callSelectTool(filteredTools[index])
    } else if (
      (e.key === 'Tab' || e.key === 'ArrowDown') &&
      !e.shiftKey &&
      index === filteredTools.length - 1
    ) {
      e.preventDefault()
      itemsRef.current[0]?.focus()
    } else if (e.key === 'ArrowUp' && !e.shiftKey && index === 0) {
      // go to last element if arrow up is pressed on first element
      e.preventDefault()
      itemsRef.current[itemsRef.current.length - 1]?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prevIndex = index - 1 >= 0 ? index - 1 : itemsRef.current.length - 1
      itemsRef.current[prevIndex]?.focus()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      const nextIndex = index + 1 < itemsRef.current.length ? index + 1 : 0
      itemsRef.current[nextIndex]?.focus()
    }
  }

  const toolsSupported = LLM_LIST.find(
    (llm) => llm.modelId === chatSettings?.model || llm.hostedId === chatSettings?.model
  )?.tools

  return (
    <>
      {isToolPickerOpen && (
        <div className="bg-background flex max-h-80 w-full min-w-0 flex-col space-y-1 overflow-y-auto rounded-xl border p-2 text-sm">
          {!toolsSupported ? (
            <div className="text-md hover:bg-accent flex h-14 cursor-pointer items-center justify-center italic">
              This model does not support plugins. Select a different model.
            </div>
          ) : filteredTools.length === 0 ? (
            <div className="text-md hover:bg-accent flex h-14 cursor-pointer items-center justify-center italic">
              No matching tools.
            </div>
          ) : (
            <>
              {filteredTools.map((item, index) => (
                <div
                  key={item.id}
                  ref={(ref) => {
                    itemsRef.current[index] = ref
                  }}
                  tabIndex={0}
                  className="hover:bg-accent focus:bg-accent flex cursor-pointer items-center rounded p-2 focus:outline-none"
                  onClick={() => callSelectTool(item as Tables<'tools'>)}
                  onKeyDown={getKeyDownHandler(index)}
                >
                  <div className={'w-[32px]'}>
                    <IconApps size={32} />
                  </div>
                  <div className="ml-3 flex flex-col">
                    <div className="flex items-center font-bold">
                      {item.name}

                      {/* Render badges for tools */}
                      <div className="ml-2 flex items-center space-x-1">
                        {/* Badge indicator (Pro, Ultra, Super, etc) */}
                        {(item as any).badge && (
                          <Badge variant={'outline'} className={'h-5 text-xs capitalize'}>
                            {(item as any).badge}
                          </Badge>
                        )}
                        {/* No extra Upgrade chip; badge already signals tier */}

                        {/* New badge */}
                        {(item as any).isNew && (
                          <Badge className="h-5 bg-violet-700 text-xs">New</Badge>
                        )}

                        {/* Additional badge if present */}
                        {(item as any).additionalBadge && (
                          <Badge variant={'outline'} className={'h-5 text-xs capitalize'}>
                            {(item as any).additionalBadge}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="line-clamp-1 max-w-full overflow-hidden text-ellipsis opacity-60">
                      {item.description}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </>
  )
}
